import { Button, cn } from '@alicerce/ui';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

export interface TourStop {
  /** Seletores do elemento destacado; vale o primeiro que estiver visível (o menu muda no celular). */
  targets: string[];
  title: string;
  body: string;
  /** Texto quando nenhum alvo está na tela. */
  fallbackBody?: string;
}

/** Paradas do tour de boas-vindas pelo painel. */
export const WELCOME_TOUR: TourStop[] = [
  {
    targets: ['nav[aria-label="Menu principal"]', 'button[aria-label="Abrir menu"]'],
    title: 'Este é o menu',
    body: 'Aqui ficam todas as áreas do seu negócio: produtos, pedidos, loja, financeiro e o que mais você tiver contratado.',
    fallbackBody: 'No celular, o menu fica no botão de três risquinhos, no canto de cima da tela.',
  },
  {
    targets: ['[data-tour="setup-bar"]'],
    title: 'O que falta configurar',
    body: 'Esta barra mostra quanto falta para deixar tudo pronto. Ela some sozinha quando você terminar.',
  },
  {
    targets: ['[data-tour="setup-next"]'],
    title: 'Próximo passo',
    body: 'Clique aqui e a gente leva você até a página certa, explicando o que preencher. Se preferir, dá para pular um passo e voltar depois.',
  },
  {
    targets: ['button[aria-label^="Notificações"]'],
    title: 'Avisos',
    body: 'Vendas, agendamentos e avisos importantes aparecem aqui no sininho.',
  },
  {
    targets: ['nav[aria-label="Menu principal"] a[href="/ajuda"]'],
    title: 'Precisa de ajuda?',
    body: 'A Ajuda explica cada módulo passo a passo. Você pode rever este tour quando quiser pela barra de configuração.',
    fallbackBody: 'A Ajuda, no fim do menu, explica cada módulo passo a passo. Você pode rever este tour quando quiser pela barra de configuração.',
  },
];

const PAD = 6;
const GAP = 12;

function visibleTarget(selectors: string[]): HTMLElement | null {
  for (const sel of selectors) {
    const el = document.querySelector<HTMLElement>(sel);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

function reducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Onde o cartão fica: ao lado de alvos altos (menu), embaixo ou em cima dos outros, sempre dentro da tela. */
export function placeCard(rect: { top: number; left: number; right: number; bottom: number; height: number }, card: { width: number; height: number }, view: { width: number; height: number }) {
  const clampX = (x: number) => Math.max(16, Math.min(x, view.width - card.width - 16));
  const clampY = (y: number) => Math.max(16, Math.min(y, view.height - card.height - 16));
  if (rect.height > view.height * 0.5 && rect.right + GAP + card.width <= view.width - 16) {
    return { top: clampY(rect.top + 40), left: rect.right + GAP };
  }
  if (rect.bottom + GAP + card.height <= view.height - 16) return { top: rect.bottom + GAP, left: clampX(rect.left) };
  if (rect.top - GAP - card.height >= 16) return { top: rect.top - GAP - card.height, left: clampX(rect.left) };
  return { top: clampY(view.height - card.height - 16), left: clampX(rect.left) };
}

/**
 * Tour com destaque: escurece a tela, recorta o elemento da parada e mostra um cartão explicando.
 * Teclado: setas avançam e voltam, Esc fecha. Sem o alvo na tela, o cartão aparece no centro.
 */
export function Spotlight({ stops, onClose, onFinish, finishLabel = 'Concluir' }: { stops: TourStop[]; onClose: () => void; onFinish: () => void; finishLabel?: string }) {
  const [index, setIndex] = useState(0);
  // O retângulo vale só para a parada em que foi medido; sem alvo na tela, o cartão vai para o centro.
  const [measured, setMeasured] = useState<{ index: number; rect: DOMRect | null }>({ index: -1, rect: null });
  const rect = measured.index === index ? measured.rect : null;
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const stop = stops[index]!;
  const last = index === stops.length - 1;

  // Acompanha o alvo da parada: rola até ele e reposiciona quando a tela muda de tamanho ou rola.
  useLayoutEffect(() => {
    const el = visibleTarget(stop.targets);
    if (!el) return;
    el.scrollIntoView?.({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
    const update = () => {
      const r = el.getBoundingClientRect();
      setMeasured({ index, rect: r.width > 0 && r.height > 0 ? r : null });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [stop, index]);

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!rect || !card) {
      setPos(null);
      return;
    }
    setPos(placeCard(rect, { width: card.offsetWidth, height: card.offsetHeight }, { width: window.innerWidth, height: window.innerHeight }));
  }, [rect]);

  useEffect(() => {
    primaryRef.current?.focus();
  }, [index]);

  const next = () => (last ? onFinish() : setIndex((i) => i + 1));
  const back = () => setIndex((i) => Math.max(0, i - 1));

  return (
    <div
      className="fixed inset-0 z-[60]"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
        else if (e.key === 'ArrowRight') next();
        else if (e.key === 'ArrowLeft') back();
      }}
    >
      {rect ? (
        <div
          aria-hidden
          className="setup-spotlight pointer-events-none fixed rounded-2xl ring-2 ring-white/90"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: '0 0 0 9999px rgb(16 34 58 / 0.55)',
          }}
        />
      ) : (
        <div aria-hidden className="fixed inset-0 bg-brand-900/55" />
      )}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'setup-pop fixed w-[min(22rem,calc(100vw-2rem))] rounded-tile border border-white bg-white p-5 text-ink shadow-[0_30px_80px_-30px_rgb(16_34_58/0.7)]',
          !pos && 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
        )}
        style={pos ?? undefined}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs font-semibold tracking-wide text-violet uppercase">
            {index + 1} de {stops.length}
          </p>
          <button type="button" onClick={onClose} aria-label="Fechar o tour" className="-mt-1 -mr-1 rounded-full p-1 text-muted hover:bg-mist hover:text-ink">
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <h2 id={titleId} className="mt-1 text-lg font-semibold">
          {stop.title}
        </h2>
        <p className="mt-2 text-[15px] leading-relaxed text-ink/80">{!rect && stop.fallbackBody ? stop.fallbackBody : stop.body}</p>
        <div className="mt-4 flex items-center gap-1" aria-hidden>
          {stops.map((s, i) => (
            <span key={s.title} className={cn('h-1.5 rounded-full transition-all', i === index ? 'w-5 bg-brand-600' : 'w-1.5 bg-line')} />
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          {index > 0 ? (
            <Button variant="ghost" size="sm" onClick={back}>
              <ArrowLeft className="size-4" aria-hidden /> Voltar
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={onClose}>
              Pular tour
            </Button>
          )}
          <Button ref={primaryRef} onClick={next}>
            {last ? finishLabel : 'Próximo'} <ArrowRight className="size-4" aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  );
}
